import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  BadgePercent,
  Building2,
  Check,
  FileCheck2,
  HardHat,
  Landmark,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { PropertyPortalStrip } from "@/components/property-portal-strip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { billingPlanCopy, fallbackBillingPlans } from "@/lib/billing.catalog";
import { getAuthStatus } from "@/lib/auth.functions";
import { isSafeInternalPath, type AuthStatus } from "@/lib/auth.types";
import { getPublicBillingPlans } from "@/lib/billing.functions";
import { formatBillingPrice, type BillingPlan } from "@/lib/billing.types";
import { useLanguage, type LanguageCode } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type PricingCategory = "auftragnehmer" | "makler" | "inserate";

const pricingCategories: PricingCategory[] = ["auftragnehmer", "makler", "inserate"];

export const Route = createFileRoute("/anbieter-preise")({
  staticData: { sitemap: true },
  validateSearch: (
    search: Record<string, unknown>,
  ): { kategorie?: PricingCategory; next?: string } => ({
    kategorie: pricingCategories.includes(search.kategorie as PricingCategory)
      ? (search.kategorie as PricingCategory)
      : undefined,
    next:
      typeof search.next === "string" && isSafeInternalPath(search.next) ? search.next : undefined,
  }),
  loader: () => getPublicBillingPlans(),
  head: () => ({
    meta: [
      { title: "Mitgliedschaften & Immobilieninserate. Begner" },
      {
        name: "description",
        content:
          "Transparente Begner-Preise für Auftragnehmer, Immobilienmakler und Immobilieninserate.",
      },
    ],
  }),
  component: ProviderPricingPage,
});

const pricingCopy = {
  de: {
    locale: "de-CH",
    eyebrow: "Preise für Anbieter",
    title: "Mehr passende Aufträge. Planbare Kosten.",
    intro:
      "Präsentieren Sie Ihren Betrieb professionell und gewinnen Sie neue Aufträge – mit einer transparenten Monatspauschale statt Erfolgsprovision.",
    promoKicker: "Einführungsaktion · nur für kurze Zeit",
    promoTitle: "Aktuelle Angebote",
    promoText: "Der Rabatt ist bereits in allen angezeigten Preisen berücksichtigt.",
    promoBadge: "Aktionspreis",
    categoryLabel: "Preiskategorie wählen",
    categoryTabs: {
      auftragnehmer: "Auftragnehmer",
      makler: "Makler",
      inserate: "Immo-Inserate",
    },
    contractorsEyebrow: "Für Auftragnehmer",
    contractorsTitle: "Gewinnen Sie Aufträge, die zu Ihrem Betrieb passen",
    contractorsText:
      "Kunden suchen nach verlässlichen Fachbetrieben. Mit Begner zeigen Sie Kompetenz, Region und Leistungen dort, wo konkrete Projekte entstehen. Pro enthält den Eintrag im Handwerkerverzeichnis – ohne doppelte Monatsgebühr und ohne Provision pro gewonnenem Auftrag.",
    agentsEyebrow: "Für Immobilienmakler",
    agentsTitle: "Ihr professioneller Auftritt auf Begner",
    agentsText:
      "Die Mitgliedschaft umfasst das Maklerprofil. Einzelne Immobilieninserate werden separat gebucht.",
    listingsEyebrow: "Immobilien inserieren",
    listingsTitle: "Eine Laufzeit, ein klarer Preis",
    listingsText:
      "Die Laufzeit beginnt erst, wenn das geprüfte und bezahlte Inserat tatsächlich veröffentlicht wird.",
    monthly: "pro Monat",
    contractorCta: "Paket im Firmenkonto wählen",
    agentCta: "Als Makler starten",
    listingCta: "Immobilie erfassen",
    paymentTitle: "Bezahlung per Schweizer Rechnung",
    paymentText:
      "Die Freischaltung erfolgt nach bestätigtem Zahlungseingang per QR-Rechnung oder Banküberweisung. TWINT wird später ergänzt.",
    paymentPoints: [
      "Keine automatische Kreditkartenbelastung",
      "Mitgliedschaften werden im Voraus bezahlt",
      "Inseratstage zählen erst ab Veröffentlichung",
    ],
    vatHint: "Nicht MWST-pflichtig – es wird keine MWST ausgewiesen.",
    faqTitle: "Häufige Fragen zur Mitgliedschaft",
    faqIntro:
      "Transparent beantwortet, damit Sie die passende Entscheidung für Ihren Betrieb treffen können.",
    faqs: [
      {
        question: "Gibt es eine Provision auf gewonnene Aufträge?",
        answer:
          "Nein. Sie zahlen ausschliesslich die ausgewiesene Monatspauschale. Wie viele Aufträge Sie über Begner gewinnen, beeinflusst den Preis nicht.",
      },
      {
        question: "Was ist im Pro-Paket enthalten?",
        answer:
          "Pro kombiniert Ihr Firmenprofil mit Interessebekundungen an geprüften Aufträgen. Sie bewerben sich innerhalb von 72 Stunden; danach wählt der Auftraggeber bis zu fünf Firmen für die Kontaktaufnahme aus.",
      },
      {
        question: "Kann ich meine Mitgliedschaft pausieren oder kündigen?",
        answer:
          "Ja. Sie behalten die volle Kontrolle und können die Mitgliedschaft jeweils zum Ende der bezahlten Laufzeit anpassen oder beenden.",
      },
      {
        question: "Wann wird mein Profil sichtbar?",
        answer:
          "Nach der persönlichen Prüfung Ihres Unternehmensprofils wird Ihr Eintrag freigeschaltet. So bleibt das Verzeichnis für Kunden verlässlich.",
      },
      {
        question: "Wie funktionieren Aktionen und Rabattcodes?",
        answer:
          "Der Aktionsrabatt ist bereits in allen angezeigten Preisen eingerechnet. Auf Ihrer Rechnung erscheint somit direkt der reduzierte Betrag – Rabattcodes können Sie bei der Bestellung prüfen und einlösen. Es gilt das günstigere Angebot; Rabatte werden nicht addiert.",
      },
      {
        question: "Wie erhalte ich Zugang zu neuen Aufträgen?",
        answer:
          "Veröffentlichte Aufträge sind für alle sichtbar. Mit geprüftem Konto und Auftragnehmer Pro können Sie während der 72-Stunden-Frist Interesse bekunden. Kundendaten erhalten nur die bis zu fünf ausgewählten Firmen. Das Verzeichnis-Paket beinhaltet keine Interessebekundungen.",
      },
      {
        question: "Fallen zusätzliche Gebühren pro Kontakt an?",
        answer:
          "Nein. Pro enthält Interessebekundungen ohne zusätzliche Einzelgebühr. Eine Bewerbung oder Zahlung garantiert keine Auswahl durch den Auftraggeber.",
      },
      {
        question: "Kann ich selbst auswählen, auf welche Aufträge ich reagiere?",
        answer:
          "Ja. Sie prüfen Kategorie, Ort, Projektumfang, Zeitraum und Budget und entscheiden vollständig selbst, welche Anfragen für Ihren Betrieb interessant sind.",
      },
      {
        question: "Sind Immobilieninserate in der Makler-Mitgliedschaft enthalten?",
        answer:
          "Die Mitgliedschaft umfasst Ihr öffentliches Maklerprofil. Immobilieninserate werden separat mit 14 oder 30 Tagen Laufzeit gebucht, damit Sie nur für tatsächlich benötigte Inserate bezahlen.",
      },
      {
        question: "Wann beginnt die Laufzeit eines Immobilieninserats?",
        answer:
          "Erst nach unserer Prüfung, bestätigtem Zahlungseingang und der tatsächlichen Veröffentlichung. Die Zeit für Prüfung und Bezahlung wird nicht von Ihren Inseratstagen abgezogen.",
      },
      {
        question: "Wie erfolgt die Bezahlung?",
        answer:
          "Sie erhalten eine Schweizer QR-Rechnung und bezahlen per Banküberweisung. Es gibt keine automatische Belastung einer Kreditkarte.",
      },
      {
        question: "Kann ich später in ein anderes Paket wechseln?",
        answer:
          "Ja. Sie können Ihr Paket passend zu Ihrer aktuellen Auftragslage anpassen. Änderungen gelten jeweils für die nächste bezahlte Laufzeit.",
      },
    ],
  },
  fr: {
    locale: "fr-CH",
    eyebrow: "Prix pour les professionnels",
    title: "Commencer simplement. Grandir à son rythme.",
    intro:
      "Des abonnements clairs pour les prestataires et courtiers, ainsi que des durées fixes pour les annonces immobilières.",
    promoKicker: "Offre de lancement · durée limitée",
    promoTitle: "Offres actuelles",
    promoText: "La réduction est déjà incluse dans tous les prix affichés.",
    promoBadge: "Prix promotionnel",
    categoryLabel: "Choisir une catégorie de prix",
    categoryTabs: {
      auftragnehmer: "Prestataires",
      makler: "Courtiers",
      inserate: "Annonces immo",
    },
    contractorsEyebrow: "Pour les prestataires",
    contractorsTitle: "Être visible ou gagner de nouveaux mandats",
    contractorsText:
      "L’offre Pro comprend déjà la fiche dans l’annuaire. Il n’y a pas de double mensualité.",
    agentsEyebrow: "Pour les courtiers immobiliers",
    agentsTitle: "Votre présence professionnelle sur Begner",
    agentsText:
      "L’abonnement comprend le profil de courtier. Les annonces sont réservées séparément.",
    listingsEyebrow: "Publier un bien",
    listingsTitle: "Une durée, un prix clair",
    listingsText:
      "La durée commence uniquement lorsque l’annonce vérifiée et payée est effectivement publiée.",
    monthly: "par mois",
    contractorCta: "Choisir une offre entreprise",
    agentCta: "Devenir courtier",
    listingCta: "Saisir un bien",
    paymentTitle: "Paiement par facture suisse",
    paymentText:
      "L’activation intervient après confirmation du paiement par facture QR ou virement bancaire. TWINT sera ajouté ultérieurement.",
    paymentPoints: [
      "Aucun débit automatique de carte",
      "Abonnements payés à l’avance",
      "La durée d’annonce commence à la publication",
    ],
    vatHint: "Non assujetti à la TVA – aucune TVA n’est indiquée.",
    faqTitle: "Questions fréquentes sur l’abonnement",
    faqIntro: "Des réponses transparentes pour choisir l’offre adaptée à votre entreprise.",
    faqs: [
      {
        question: "Y a-t-il une commission sur les mandats remportés ?",
        answer:
          "Non. Vous payez uniquement le forfait mensuel affiché, quel que soit le nombre de mandats obtenus.",
      },
      {
        question: "Que comprend l’offre Pro ?",
        answer:
          "Elle réunit votre profil d’entreprise et les intérêts pour des mandats vérifiés. Postulez dans les 72 heures ; le client choisit ensuite jusqu’à cinq entreprises à contacter.",
      },
      {
        question: "Puis-je suspendre ou résilier ?",
        answer:
          "Oui. Vous gardez le contrôle et pouvez adapter ou arrêter votre abonnement à la fin de la période payée.",
      },
      {
        question: "Quand mon profil est-il visible ?",
        answer:
          "Votre profil est publié après vérification personnelle afin de préserver la qualité de l’annuaire.",
      },
      {
        question: "Comment fonctionnent les offres et codes de réduction ?",
        answer:
          "La réduction est déjà incluse dans tous les prix affichés et le montant réduit figure directement sur votre facture. Aucun code n’est nécessaire.",
      },
      {
        question: "Comment accéder aux nouveaux mandats ?",
        answer:
          "Les mandats publiés sont visibles par tous. Un compte validé avec Pro permet de manifester un intérêt pendant 72 heures. Seules les cinq entreprises au maximum choisies par le client reçoivent ses coordonnées. L’offre Annuaire ne comprend pas les intérêts.",
      },
      {
        question: "Y a-t-il des frais supplémentaires par contact ?",
        answer:
          "Non. Pro comprend les intérêts sans frais individuels. Une candidature ou un paiement ne garantit pas votre sélection par le client.",
      },
      {
        question: "Puis-je choisir les mandats auxquels je réponds ?",
        answer:
          "Oui. Vous consultez la catégorie, le lieu, l’ampleur, le délai et le budget, puis choisissez librement les demandes adaptées à votre entreprise.",
      },
      {
        question: "Les annonces sont-elles incluses dans l’abonnement courtier ?",
        answer:
          "L’abonnement comprend votre profil public. Les annonces immobilières de 14 ou 30 jours sont réservées séparément afin de ne payer que celles dont vous avez besoin.",
      },
      {
        question: "Quand commence la durée d’une annonce ?",
        answer:
          "Uniquement après notre vérification, la confirmation du paiement et la publication effective. Le temps de traitement n’est pas déduit de la durée.",
      },
      {
        question: "Comment s’effectue le paiement ?",
        answer:
          "Vous recevez une facture QR suisse et payez par virement bancaire. Aucune carte de crédit n’est débitée automatiquement.",
      },
      {
        question: "Puis-je changer d’offre plus tard ?",
        answer:
          "Oui. Vous pouvez adapter votre offre à votre activité. Les changements prennent effet pour la prochaine période payée.",
      },
    ],
  },
  it: {
    locale: "it-CH",
    eyebrow: "Prezzi per professionisti",
    title: "Iniziare con semplicità. Crescere al momento giusto.",
    intro: "Abbonamenti chiari per imprese e agenti, più durate fisse per gli annunci immobiliari.",
    promoKicker: "Offerta di lancio · per un periodo limitato",
    promoTitle: "Offerte attuali",
    promoText: "Lo sconto è già incluso in tutti i prezzi visualizzati.",
    promoBadge: "Prezzo promozionale",
    categoryLabel: "Scegli la categoria di prezzo",
    categoryTabs: {
      auftragnehmer: "Imprese",
      makler: "Agenti",
      inserate: "Annunci immobiliari",
    },
    contractorsEyebrow: "Per le imprese",
    contractorsTitle: "Essere visibili o acquisire nuovi incarichi",
    contractorsText:
      "Il pacchetto Pro include già la presenza nell’elenco. Non ci sono due canoni mensili.",
    agentsEyebrow: "Per agenti immobiliari",
    agentsTitle: "La vostra presenza professionale su Begner",
    agentsText:
      "L’abbonamento include il profilo agente. I singoli annunci vengono acquistati separatamente.",
    listingsEyebrow: "Pubblicare un immobile",
    listingsTitle: "Una durata, un prezzo chiaro",
    listingsText:
      "La durata inizia solo quando l’annuncio verificato e pagato viene effettivamente pubblicato.",
    monthly: "al mese",
    contractorCta: "Scegliere un’offerta impresa",
    agentCta: "Inizia come agente",
    listingCta: "Inserisci un immobile",
    paymentTitle: "Pagamento con fattura svizzera",
    paymentText:
      "L’attivazione avviene dopo la conferma del pagamento con fattura QR o bonifico bancario. TWINT sarà aggiunto in seguito.",
    paymentPoints: [
      "Nessun addebito automatico sulla carta",
      "Abbonamenti pagati in anticipo",
      "I giorni decorrono dalla pubblicazione",
    ],
    vatHint: "Non soggetto a IVA – l’IVA non viene indicata.",
    faqTitle: "Domande frequenti sull’abbonamento",
    faqIntro: "Risposte trasparenti per scegliere l’offerta giusta per la vostra impresa.",
    faqs: [
      {
        question: "Ci sono commissioni sugli incarichi acquisiti?",
        answer:
          "No. Pagate solo il canone mensile indicato, indipendentemente dal numero di incarichi ottenuti.",
      },
      {
        question: "Cosa include il pacchetto Pro?",
        answer:
          "Unisce il profilo aziendale agli interessi per incarichi verificati. Ci si candida entro 72 ore; poi il cliente sceglie fino a cinque imprese da contattare.",
      },
      {
        question: "Posso sospendere o annullare?",
        answer:
          "Sì. Mantenete il pieno controllo e potete adattare o terminare l’abbonamento alla fine del periodo pagato.",
      },
      {
        question: "Quando il mio profilo diventa visibile?",
        answer:
          "Il profilo viene pubblicato dopo una verifica personale per garantire la qualità dell’elenco.",
      },
      {
        question: "Come funzionano le promozioni e i codici sconto?",
        answer:
          "Lo sconto è già incluso in tutti i prezzi visualizzati e in fattura compare direttamente l’importo ridotto. Non serve alcun codice.",
      },
      {
        question: "Come accedo ai nuovi incarichi?",
        answer:
          "Gli incarichi pubblicati sono visibili a tutti. Con un account approvato e Pro si può manifestare interesse entro 72 ore. Solo le cinque imprese al massimo selezionate ricevono i contatti. Il pacchetto Elenco non include gli interessi.",
      },
      {
        question: "Ci sono costi aggiuntivi per singolo contatto?",
        answer:
          "No. Pro include gli interessi senza costi individuali. Una candidatura o un pagamento non garantisce la selezione da parte del cliente.",
      },
      {
        question: "Posso scegliere a quali incarichi rispondere?",
        answer:
          "Sì. Può esaminare categoria, luogo, portata, tempistica e budget e decidere liberamente quali richieste sono adatte alla sua impresa.",
      },
      {
        question: "Gli annunci sono inclusi nell’abbonamento agente?",
        answer:
          "L’abbonamento comprende il profilo pubblico. Gli annunci di 14 o 30 giorni vengono acquistati separatamente, così paga solo quelli necessari.",
      },
      {
        question: "Quando inizia la durata di un annuncio?",
        answer:
          "Solo dopo la verifica, la conferma del pagamento e la pubblicazione effettiva. Il tempo di elaborazione non viene sottratto dalla durata.",
      },
      {
        question: "Come avviene il pagamento?",
        answer:
          "Riceverà una fattura QR svizzera da pagare tramite bonifico bancario. Non vi è alcun addebito automatico sulla carta di credito.",
      },
      {
        question: "Posso cambiare pacchetto in seguito?",
        answer:
          "Sì. Può adattare il pacchetto alla situazione attuale. Le modifiche valgono per il periodo di pagamento successivo.",
      },
    ],
  },
  en: {
    locale: "en-CH",
    eyebrow: "Pricing for professionals",
    title: "Start simply. Grow when it fits.",
    intro:
      "Clear memberships for contractors and agents, plus fixed periods for property listings.",
    promoKicker: "Launch offer · limited time",
    promoTitle: "Current offers",
    promoText: "The discount is already included in every price shown.",
    promoBadge: "Promotional price",
    categoryLabel: "Choose a pricing category",
    categoryTabs: {
      auftragnehmer: "Contractors",
      makler: "Agents",
      inserate: "Property listings",
    },
    contractorsEyebrow: "For contractors",
    contractorsTitle: "Be visible or win new jobs",
    contractorsText:
      "The Pro plan already includes the directory listing. There are no duplicate monthly fees.",
    agentsEyebrow: "For real estate agents",
    agentsTitle: "Your professional presence on Begner",
    agentsText:
      "Membership includes the agent profile. Individual property listings are booked separately.",
    listingsEyebrow: "List a property",
    listingsTitle: "One duration, one clear price",
    listingsText: "The term starts only when the reviewed and paid listing is actually published.",
    monthly: "per month",
    contractorCta: "Choose a company plan",
    agentCta: "Start as an agent",
    listingCta: "Create property listing",
    paymentTitle: "Payment by Swiss invoice",
    paymentText:
      "Access is activated after payment by QR-bill or bank transfer is confirmed. TWINT will be added later.",
    paymentPoints: [
      "No automatic card charge",
      "Memberships are paid in advance",
      "Listing days start at publication",
    ],
    vatHint: "Not subject to VAT – no VAT is shown.",
    faqTitle: "Frequently asked membership questions",
    faqIntro: "Straight answers to help you choose the right plan for your business.",
    faqs: [
      {
        question: "Is there a commission on jobs I win?",
        answer: "No. You only pay the stated monthly fee, regardless of how many jobs you win.",
      },
      {
        question: "What is included in Pro?",
        answer:
          "It combines your company profile with expressions of interest in verified jobs. Apply within 72 hours; the customer then selects up to five companies to contact them.",
      },
      {
        question: "Can I pause or cancel?",
        answer:
          "Yes. You stay in control and can adjust or end membership at the close of the paid period.",
      },
      {
        question: "When will my profile be visible?",
        answer:
          "Your profile is published after a personal review, maintaining a reliable directory for customers.",
      },
      {
        question: "How do promotions and discount codes work?",
        answer:
          "The discount is already included in every displayed price, and the reduced amount appears directly on your invoice. You can check and redeem discount codes when ordering. The better offer applies; discounts are not combined.",
      },
      {
        question: "How do I access new jobs?",
        answer:
          "Published jobs are visible to everyone. An approved account with Contractor Pro lets you express interest during the 72-hour window. Only up to five companies selected by the customer receive contact details. The Directory plan does not include interest submissions.",
      },
      {
        question: "Are there extra charges per contact?",
        answer:
          "No. Pro includes expressions of interest without individual fees. Applying or paying does not guarantee that the customer will select your company.",
      },
      {
        question: "Can I choose which jobs to respond to?",
        answer:
          "Yes. Review the category, location, scope, timing and budget, then decide independently which enquiries suit your business.",
      },
      {
        question: "Are listings included in the agent membership?",
        answer:
          "Membership includes your public agent profile. Individual 14- or 30-day listings are booked separately, so you only pay for the listings you need.",
      },
      {
        question: "When does a property listing term begin?",
        answer:
          "Only after our review, confirmed payment and actual publication. Review and payment time is not deducted from your listing term.",
      },
      {
        question: "How do I pay?",
        answer:
          "You receive a Swiss QR invoice and pay by bank transfer. There is no automatic credit-card charge.",
      },
      {
        question: "Can I switch plans later?",
        answer:
          "Yes. You can adapt your plan to your current workload. Changes apply to the next paid period.",
      },
    ],
  },
} as const;

function PlanCard({
  plan,
  language,
  locale,
  monthly,
  featured = false,
  action,
}: {
  plan: BillingPlan;
  language: LanguageCode;
  locale: string;
  monthly: string;
  featured?: boolean;
  action: React.ReactNode;
}) {
  const copy = billingPlanCopy[language][plan.code];
  return (
    <Card
      className={cn(
        "relative flex h-full flex-col overflow-hidden border-border/70 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-brand/50 hover:shadow-brand focus-within:-translate-y-1 focus-within:border-brand/50 focus-within:shadow-brand",
        featured && "border-primary/40 shadow-brand",
      )}
    >
      {featured ? <div className="h-1.5 bg-gradient-brand" /> : null}
      <CardHeader className="space-y-4 pb-4">
        <div className="flex min-h-7 items-start justify-between gap-3">
          <h3 className="font-display text-xl font-bold">{copy.name}</h3>
          {copy.badge ? <Badge className="shrink-0">{copy.badge}</Badge> : null}
        </div>
        <p className="min-h-12 text-sm leading-6 text-muted-foreground">{copy.description}</p>
        <div>
          <div className="mb-1.5 flex items-center gap-2">
            <span className="text-sm font-semibold text-muted-foreground line-through decoration-2">
              {formatBillingPrice(plan.regularPriceMinor, locale, plan.currency)}
            </span>
            <Badge className="border-0 bg-amber-400 text-amber-950 hover:bg-amber-400">
              {`${Math.round((1 - plan.priceMinor / plan.regularPriceMinor) * 100)} %`}
            </Badge>
          </div>
          <span className="font-display text-4xl font-extrabold tracking-tight text-brand-deep">
            {formatBillingPrice(plan.priceMinor, locale, plan.currency)}
          </span>
          {plan.billingInterval === "month" ? (
            <span className="ml-2 text-sm font-medium text-muted-foreground">{monthly}</span>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        <ul className="space-y-3 text-sm">
          {copy.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2.5">
              <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-brand-soft text-brand-deep">
                <Check className="size-3.5" />
              </span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <div className="mt-7 pt-1">{action}</div>
      </CardContent>
    </Card>
  );
}

function ProviderPricingPage() {
  const loaded = Route.useLoaderData();
  const { kategorie, next } = Route.useSearch();
  const navigate = Route.useNavigate();
  const checkAuth = useServerFn(getAuthStatus);
  const [account, setAccount] = useState<AuthStatus | null>(null);
  const [checkingAccount, setCheckingAccount] = useState(true);
  useEffect(() => {
    let active = true;
    void checkAuth()
      .then((status) => {
        if (active) setAccount(status);
      })
      .finally(() => {
        if (active) setCheckingAccount(false);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [checkAuth]);
  const plans = loaded.plans.length ? loaded.plans : fallbackBillingPlans;
  const { language } = useLanguage();
  const copy = pricingCopy[language];
  const getPlan = (code: BillingPlan["code"]) =>
    plans.find((plan) => plan.code === code) ??
    fallbackBillingPlans.find((plan) => plan.code === code)!;

  const billingUrl = `/konto${next ? `?next=${encodeURIComponent(next)}` : ""}#billing`;
  const contractorUrl = account?.authenticated
    ? billingUrl
    : `/login?mode=registrieren&typ=auftragnehmer&next=${encodeURIComponent(billingUrl)}`;
  const contractorAction = checkingAccount ? (
    <Button disabled className="w-full">
      {copy.contractorCta}
    </Button>
  ) : (
    <Button asChild className="w-full">
      <a href={contractorUrl}>
        {copy.contractorCta}
        <ArrowRight className="ml-2 size-4" />
      </a>
    </Button>
  );

  return (
    <main className="overflow-x-clip">
      <section className="relative isolate border-b border-border/70 bg-muted/30 py-10 sm:py-24">
        <div className="pointer-events-none absolute left-1/2 top-0 -z-10 h-72 w-[70rem] -translate-x-1/2 rounded-full bg-brand-soft/80 blur-3xl" />
        <div className="container-page text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-deep">
            {copy.eyebrow}
          </p>
          <h1 className="mx-auto mt-4 max-w-4xl font-display text-3xl font-extrabold tracking-tight min-[390px]:text-4xl sm:mt-5 sm:text-6xl">
            {copy.title}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-muted-foreground sm:mt-5 sm:text-lg sm:leading-8">
            {copy.intro}
          </p>
          <div className="mx-auto mt-7 flex max-w-2xl items-start gap-3 rounded-2xl border border-amber-300/70 bg-amber-300 px-4 py-4 text-left text-amber-950 shadow-[0_18px_45px_-20px_oklch(0.75_0.16_75)] sm:mt-8 sm:items-center sm:gap-4 sm:px-6">
            <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-amber-950 text-amber-200">
              <BadgePercent className="size-6" aria-hidden />
            </span>
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.16em]">
                {copy.promoKicker}
              </p>
              <p className="mt-0.5 font-display text-xl font-extrabold sm:text-2xl">
                {copy.promoTitle}
              </p>
              <p className="mt-1 text-sm font-medium text-amber-950/75">{copy.promoText}</p>
            </div>
          </div>
        </div>
      </section>

      <Tabs
        value={kategorie ?? "auftragnehmer"}
        onValueChange={(value) =>
          void navigate({
            to: "/anbieter-preise",
            search: { kategorie: value as PricingCategory, next },
            replace: true,
            resetScroll: false,
          })
        }
      >
        <div className="sticky top-16 z-30 border-b border-border/70 bg-background/95 py-3 backdrop-blur sm:py-4">
          <div className="container-page">
            <TabsList
              aria-label={copy.categoryLabel}
              className="grid h-auto w-full grid-cols-3 gap-1.5 rounded-2xl bg-muted/80 p-1.5 sm:gap-2"
            >
              <TabsTrigger
                value="auftragnehmer"
                className="min-h-12 whitespace-normal rounded-xl px-2 py-2.5 text-xs font-semibold leading-tight sm:text-sm data-[state=active]:shadow-card"
              >
                <HardHat className="mr-1.5 hidden size-4 shrink-0 sm:block" />
                {copy.categoryTabs.auftragnehmer}
              </TabsTrigger>
              <TabsTrigger
                value="makler"
                className="min-h-12 whitespace-normal rounded-xl px-2 py-2.5 text-xs font-semibold leading-tight sm:text-sm data-[state=active]:shadow-card"
              >
                <Building2 className="mr-1.5 hidden size-4 shrink-0 sm:block" />
                {copy.categoryTabs.makler}
              </TabsTrigger>
              <TabsTrigger
                value="inserate"
                className="min-h-12 whitespace-normal rounded-xl px-2 py-2.5 text-xs font-semibold leading-tight sm:text-sm data-[state=active]:shadow-card"
              >
                <FileCheck2 className="mr-1.5 hidden size-4 shrink-0 sm:block" />
                {copy.categoryTabs.inserate}
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

        <TabsContent value="auftragnehmer" className="mt-0">
          <section className="container-page pb-12 pt-8 sm:pb-16 sm:pt-10">
            <div className="grid gap-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-start">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-deep">
                  {copy.contractorsEyebrow}
                </p>
                <h2 className="mt-3 font-display text-3xl font-bold tracking-tight">
                  {copy.contractorsTitle}
                </h2>
                <p className="mt-4 max-w-xl leading-7 text-muted-foreground">
                  {copy.contractorsText}
                </p>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <PlanCard
                  plan={getPlan("contractor_directory")}
                  language={language}
                  locale={copy.locale}
                  monthly={copy.monthly}
                  action={contractorAction}
                />
                <PlanCard
                  plan={getPlan("contractor_pro")}
                  language={language}
                  locale={copy.locale}
                  monthly={copy.monthly}
                  featured
                  action={contractorAction}
                />
              </div>
            </div>
          </section>
        </TabsContent>

        <TabsContent value="makler" className="mt-0">
          <section className="container-page pb-12 pt-8 sm:pb-16 sm:pt-10">
            <div className="grid gap-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-start">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-deep">
                  {copy.agentsEyebrow}
                </p>
                <h2 className="mt-3 font-display text-3xl font-bold tracking-tight">
                  {copy.agentsTitle}
                </h2>
                <p className="mt-4 max-w-xl leading-7 text-muted-foreground">{copy.agentsText}</p>
              </div>
              <div className="max-w-xl">
                <PlanCard
                  plan={getPlan("agent_membership")}
                  language={language}
                  locale={copy.locale}
                  monthly={copy.monthly}
                  action={
                    <Button asChild className="w-full">
                      <Link to="/registrieren" search={{ typ: "immobilienmakler" }}>
                        {copy.agentCta}
                        <ArrowRight className="ml-2 size-4" />
                      </Link>
                    </Button>
                  }
                />
              </div>
            </div>
            <PropertyPortalStrip compact className="mt-10" />
          </section>
        </TabsContent>

        <TabsContent value="inserate" className="mt-0">
          <section className="container-page pb-12 pt-8 sm:pb-16 sm:pt-10">
            <div className="grid gap-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-start">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-deep">
                  {copy.listingsEyebrow}
                </p>
                <h2 className="mt-3 font-display text-3xl font-bold tracking-tight">
                  {copy.listingsTitle}
                </h2>
                <p className="mt-4 max-w-xl leading-7 text-muted-foreground">{copy.listingsText}</p>
              </div>
              <div className="grid gap-5 md:grid-cols-2">
                {["property_14_days", "property_30_days"].map((code) => (
                  <PlanCard
                    key={code}
                    plan={getPlan(code as BillingPlan["code"])}
                    language={language}
                    locale={copy.locale}
                    monthly={copy.monthly}
                    featured={code === "property_30_days"}
                    action={
                      <Button
                        asChild
                        variant={code === "property_30_days" ? "default" : "outline"}
                        className="w-full"
                      >
                        <Link
                          to="/immobilien"
                          search={{ paket: code as "property_14_days" | "property_30_days" }}
                        >
                          {copy.listingCta}
                          <ArrowRight className="ml-2 size-4" />
                        </Link>
                      </Button>
                    }
                  />
                ))}
              </div>
            </div>
            <PropertyPortalStrip compact className="mt-10" />
          </section>
        </TabsContent>
      </Tabs>

      <section className="container-page pb-14 sm:pb-20">
        <div className="mx-auto max-w-3xl">
          <p className="text-center text-xs font-bold uppercase tracking-[0.18em] text-brand-deep">
            {copy.contractorsEyebrow}
          </p>
          <h2 className="mt-3 text-center font-display text-3xl font-bold tracking-tight">
            {copy.faqTitle}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center leading-7 text-muted-foreground">
            {copy.faqIntro}
          </p>
          <Accordion
            type="single"
            collapsible
            className="mt-8 rounded-2xl border border-border bg-card px-5 sm:px-7"
          >
            {copy.faqs.map((faq, index) => (
              <AccordionItem key={faq.question} value={`membership-faq-${index}`}>
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="max-w-2xl text-sm leading-6 text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="container-page pb-20 sm:pb-24">
        <div className="overflow-hidden rounded-3xl border border-primary/15 bg-brand-soft/60 p-7 sm:p-10">
          <div className="grid gap-8 lg:grid-cols-[auto_minmax(0,1fr)_minmax(280px,0.7fr)] lg:items-center">
            <span className="grid size-12 place-items-center rounded-2xl bg-background text-brand-deep shadow-sm">
              <Landmark className="size-6" />
            </span>
            <div>
              <h2 className="font-display text-2xl font-bold">{copy.paymentTitle}</h2>
              <p className="mt-3 max-w-2xl leading-7 text-muted-foreground">{copy.paymentText}</p>
              <p className="mt-3 text-xs text-muted-foreground">{copy.vatHint}</p>
            </div>
            <ul className="space-y-3 text-sm font-medium">
              {copy.paymentPoints.map((point) => (
                <li key={point} className="flex items-start gap-2">
                  <Check className="mt-0.5 size-4 shrink-0 text-brand-deep" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </main>
  );
}
